import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { setEducationLevelSubjectList } from './ELSSlice';

import { ELS, ELSPayload, ELSResponse, ELSPayloadEdit } from '../interfaces/ELS.interfaces';

import { GroupAreaAll } from '../interfaces/groupsArea.interfaces';
import { AccessState } from '../../../../types';

export const educationLevelSubjectsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['EducationLevelSubjects'],
});
// Define a service using a base URL and expected endpoints
export const educationLevelSubjectsApi = educationLevelSubjectsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all educationLevelSubjects
     */
    getEducationLevelSubjects: builder.query<
      ELSResponse,
      { profileId: number; page: number; perPage: number; search: string }
    >({
      queryFn: async ({ profileId, page, search, perPage }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<ELSResponse>('/group-area-subjects', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'desc',
              search,
            },
          });

          dispatch(setEducationLevelSubjectList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'EducationLevelSubjects', id } as const)),
              { type: 'EducationLevelSubjects', id: 'LIST' },
            ]
          : [{ type: 'EducationLevelSubjects', id: 'LIST' }],
    }),

    /**
     * @GET group subject educationLevelSubjects
     */
    getEducationLevelSubjectsGroup: builder.query<
      ELSResponse,
      { profileId: number; page: number; perPage: number; search: string; groupId: string }
    >({
      queryFn: async ({ profileId, page, search, perPage, groupId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<ELSResponse>(`/group-areas/${groupId}/subjects`, {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: 'desc',
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'EducationLevelSubjects', id } as const)),
              { type: 'EducationLevelSubjects', id: 'LIST' },
            ]
          : [{ type: 'EducationLevelSubjects', id: 'LIST' }],
    }),

    /**
     * @GET Group Area
     */
    getGroupsArea: builder.query<GroupAreaAll[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<GroupAreaAll[]>('/group-areas', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });
          return Array.isArray(data) ? data : [];
        } catch (error: any) {
          return error;
        }
      },
    }),

    /**
     * @GET obtener una materia de tipo nivelacion by id
     */
    getSubjectGroupArea: builder.query<ELS, { profileId?: number; nivelationSubjectId: number }>({
      queryFn: async ({ profileId, nivelationSubjectId }, { dispatch, getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };
        const user_profile_id = profileId || currentProfile?.profileId;

        try {
          const { data } = await mainApi.get<ELS>(`/group-area-subjects/${nivelationSubjectId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [
        { type: 'EducationLevelSubjects', profileId },
      ],
    }),

    /**
     * @POST create educationLevelSubject
     */
    addEducationLevelSubject: builder.mutation<
      ELS,
      { profileId: number; educationLevelSubjectPayload: Partial<ELSPayload> }
    >({
      queryFn: async ({ profileId, educationLevelSubjectPayload }) => {
        try {
          const { data } = await mainApi.post<ELS>('/group-area-subjects', {
            user_profile_id: profileId,
            ...educationLevelSubjectPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelSubjects', id: 'LIST' }],
    }),

    /**
     * @PUT update educationLevelSubject
     */
    updateEducationLevelSubject: builder.mutation<
      ELS,
      {
        profileId: number;
        educationLevelSubjectId: number;
        educationLevelSubjectPayload: Partial<ELSPayloadEdit>;
      }
    >({
      queryFn: async ({ profileId, educationLevelSubjectId, educationLevelSubjectPayload }) => {
        try {
          const { data } = await mainApi.put<ELS>(
            `/group-area-subjects/${educationLevelSubjectId}`,
            {
              user_profile_id: profileId,
              ...educationLevelSubjectPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelSubjects', id: 'LIST' }],
    }),

    /**
     * @DELETE remove educationLevelSubject
     */
    deleteEducationLevelSubject: builder.mutation<
      ELS,
      { profileId: number; educationLevelSubjectId: number }
    >({
      queryFn: async ({ profileId, educationLevelSubjectId }) => {
        try {
          const { data } = await mainApi.delete(`group-area-subjects/${educationLevelSubjectId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelSubjects', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetEducationLevelSubjectsQuery,
  useGetEducationLevelSubjectsGroupQuery,
  useAddEducationLevelSubjectMutation,
  useDeleteEducationLevelSubjectMutation,
  useGetSubjectGroupAreaQuery,
  useUpdateEducationLevelSubjectMutation,
  useGetGroupsAreaQuery,
  useLazyGetSubjectGroupAreaQuery,
} = educationLevelSubjectsApi;
